import { useEffect, useRef } from 'react';

import { AssessmentEvents } from '~/analytics';
import {
  AssessmentEventAttribute,
  AssessmentEventInteractionType,
  AssessmentEventSource,
} from '~/analytics/assessmentEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CategorizedAssessments } from '~/lib/types/survey/CategorizedAssessments';
import { extractSortedAssessments } from '~/lib/util/assessmentIndexList.util';

/**
 * Fires `Assessment Index Menu Item Interaction` with `Interaction Type=Hover`
 * each time the scroll-spy `selectedSurveyId` changes, attributing the dwell
 * time to the *previous* survey. The first selection has no predecessor, so
 * no event fires until the user moves to a different item. (GX-3402)
 */
export const useAssessmentMenuItemDwell = (
  categorizedAssessments: CategorizedAssessments,
  selectedSurveyId: string | null,
) => {
  const { trackEvent } = useAnalytics();
  const previousSurveyIdRef = useRef<string | null>(null);
  const enteredAtRef = useRef<number | null>(null);

  useEffect(() => {
    const previousId = previousSurveyIdRef.current;
    if (previousId === selectedSurveyId) {
      // Selection did not change -- this run came from a categorizedAssessments
      // or trackEvent identity change. Leave the dwell timer alone so we don't
      // reset it mid-hover and undercount Dwell Ms.
      return;
    }

    const enteredAt = enteredAtRef.current;
    const now = Date.now();

    if (previousId !== null && enteredAt !== null) {
      const sorted = extractSortedAssessments(categorizedAssessments);
      const previousSurvey = sorted.find(
        (survey) => survey.externalId === previousId,
      );
      if (previousSurvey) {
        trackEvent(AssessmentEvents.assessmentIndexMenuItemInteraction, {
          [AssessmentEventAttribute.SurveyId]: previousSurvey.externalId,
          [AssessmentEventAttribute.SurveyName]: previousSurvey.name,
          [AssessmentEventAttribute.InteractionType]:
            AssessmentEventInteractionType.Hover,
          [AssessmentEventAttribute.DwellMs]: now - enteredAt,
          [AssessmentEventAttribute.Source]:
            AssessmentEventSource.AssessmentIndexMenu,
        });
      }
    }

    previousSurveyIdRef.current = selectedSurveyId;
    enteredAtRef.current = selectedSurveyId !== null ? now : null;
  }, [selectedSurveyId, categorizedAssessments, trackEvent]);
};
